import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Plus, X, Pencil, Trash2, ClipboardList, Search, Target, Pill } from 'lucide-react';
import { isoDate } from '../../utils/time';
import { useConfirm } from '../../components/ConfirmDialog';
import NeedsReviews from './NeedsReviews';
import {
  PageHead, Panel, Register, DetailDrawer, AcStatus, Category, Deadline,
  Field, FormRow, fmtDate, money, CATEGORY_HUE, normaliseCategory,
} from './design';

const today = () => isoDate();
const cap = (s) => String(s || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
const INP = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

// The review date is the obligation, so it decides the row edge: past due is rose, inside 30 days
// is amber. A closed plan has no review coming and carries no edge.
const reviewTone = (p) => {
  if (!p.review_due || p.status === 'closed') return undefined;
  const days = Math.ceil((new Date(p.review_due) - new Date()) / 86400000);
  return days < 0 ? 'rose' : days <= 30 ? 'amber' : undefined;
};
// A wellness and reablement period (CHSP) is time-limited and goal-oriented: it has an end date, and
// what came of it is recorded when it ends.
const OUTCOMES = [
  ['achieved', 'Goals achieved'], ['partly_achieved', 'Goals partly achieved'], ['not_achieved', 'Goals not achieved'],
  ['ongoing_support', 'Needs ongoing support'], ['withdrawn', 'Withdrew'],
];
const outcomeLabel = (v) => (OUTCOMES.find((o) => o[0] === v) || [null, null])[1];
// 'under_review' is the one plan status the shared vocabulary spells differently.
const PlanStatus = ({ status }) => <AcStatus status={status === 'under_review' ? 'review due' : status} label={cap(status || 'draft')} />;

export default function AgedCareCarePlans({ clientId: fixedClient = '', embedded = false } = {}) {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(fixedClient || '');
  useEffect(() => { if (fixedClient) setClientId(fixedClient); }, [fixedClient]);
  const [plans, setPlans] = useState([]);
  const [allPlans, setAllPlans] = useState(null);   // every participant's plans, when none is chosen
  const [loadError, setLoadError] = useState(null);
  const [selected, setSelected] = useState(null); // {plan + items}
  const [loading, setLoading] = useState(false);
  const [planModal, setPlanModal] = useState(null); // {editing?}
  const [pf, setPf] = useState({});
  const [itemModal, setItemModal] = useState(false);
  const [editItem, setEditItem] = useState(null); // the planned service being corrected
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();
  // A correction belongs to the plan it was started on: closing the plan or opening another one
  // drops it. Keyed on the id so reloading the same plan after a save does not.
  const selectedId = selected ? selected.id : null;
  useEffect(() => { setEditItem(null); }, [selectedId]);

  useEffect(() => { api.get('/aged-care/clients').then((r) => setClients(r.data || [])).catch(() => {}); }, []);

  const loadPlans = useCallback(async () => {
    setLoading(true); setLoadError(null);
    try {
      if (!clientId) { setPlans([]); setSelected(null); const r = await api.get('/aged-care/care-plans'); setAllPlans(r.data || []); return; }
      const r = await api.get('/aged-care/care-plans', { params: { client_id: clientId } }); setPlans(r.data || []);
    } catch (e) { setLoadError(e.response?.data?.message || 'Could not load the care plans'); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { loadPlans(); setSelected(null); }, [loadPlans]);

  const openPlan = async (id) => { try { const r = await api.get(`/aged-care/care-plans/${id}`); setSelected(r.data); } catch (e) { toast.error(e.response?.data?.message || 'Could not open the care plan'); } };
  // The participant's current medications (eMAR), shown with the plan so medication support is
  // planned alongside the rest of their care.
  const [meds, setMeds] = useState([]);
  useEffect(() => {
    if (!clientId) { setMeds([]); return; }
    api.get('/medications', { params: { client_id: clientId, active: 'true' } })
      .then((r) => setMeds(Array.isArray(r.data) ? r.data : (r.data?.medications || []))).catch(() => setMeds([]));
  }, [clientId]);
  const newPlan = () => { setPf({ title: 'Care plan', summary: '', goals: '', status: 'draft', start_date: today(), review_due: '', clinical_notes: '', plan_type: 'standard', reablement_end: '', outcome: '', outcome_notes: '' }); setPlanModal({}); };
  const editPlan = (p) => { setPf({ ...p, plan_type: p.plan_type || 'standard', start_date: p.start_date?.slice(0, 10) || '', review_due: p.review_due?.slice(0, 10) || '', reablement_end: p.reablement_end?.slice(0, 10) || '', outcome: p.outcome || '', outcome_notes: p.outcome_notes || '' }); setPlanModal({ editing: p }); };

  const savePlan = async () => {
    if (!pf.title?.trim()) return toast.error('Title is required');
    if (pf.plan_type === 'reablement' && !pf.reablement_end) return toast.error('Give the date the reablement period ends');
    setSaving(true);
    try {
      if (planModal.editing) { await api.put(`/aged-care/care-plans/${planModal.editing.id}`, pf); if (selected?.id === planModal.editing.id) openPlan(planModal.editing.id); }
      else await api.post('/aged-care/care-plans', { client_id: clientId, ...pf });
      toast.success('Saved'); setPlanModal(null); loadPlans();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save'); } finally { setSaving(false); }
  };
  const delPlan = async (p) => {
    const ok = await confirm('The plan and the services planned under it are removed.', { title: `Delete care plan "${p.title}"?`, confirmLabel: 'Delete care plan', danger: true });
    if (!ok) return;
    try { await api.delete(`/aged-care/care-plans/${p.id}`); toast.success('Care plan deleted'); if (selected?.id === p.id) setSelected(null); loadPlans(); } catch (e) { toast.error(e.response?.data?.message || 'Could not delete the care plan'); } };
  const delItem = async (it) => {
    const ok = await confirm(`${it.service_name || 'This service'} is taken off the plan.`, { title: 'Remove this planned service?', confirmLabel: 'Remove', danger: true });
    if (!ok) return;
    try {
      await api.delete(`/aged-care/care-plan-items/${it.id}`); toast.success('Removed from the plan');
      if (editItem && editItem.id === it.id) setEditItem(null);
      openPlan(selected.id); loadPlans();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not remove it'); }
  };
  // Adding and correcting a service share the space above the list, so only one is open at a time.
  const startAdd = () => { setEditItem(null); setItemModal(true); };
  const startEdit = (it) => { setItemModal(false); setEditItem(it); };

  // Editing the open plan happens inside its own drawer; a new plan, or an edit started from the
  // list, gets a form drawer of its own. Nothing ever stacks on top of a drawer.
  const inlineEdit = !!(selected && planModal && planModal.editing && planModal.editing.id === selected.id);
  const formDrawer = !!planModal && !inlineEdit;
  // Stable, or the drawer re-runs its focus effect on every keystroke inside it.
  // Same guard as Funding: the drawer dismisses on Escape and on a click outside, and both the
  // add-service form and the inline edit now live inside it. AddItemModal keeps its own field state,
  // so the test is "a form is open" rather than "a field has content" — coarser, but it never
  // throws away typed work silently.
  const closeDrawer = useCallback(async () => {
    if (itemModal || editItem || inlineEdit) {
      const ok = await confirm('Anything you have typed and not saved will be lost.', { title: 'Close this care plan?', confirmLabel: 'Close without saving', danger: true });
      if (!ok) return;
    }
    setSelected(null); setPlanModal(null); setItemModal(false); setEditItem(null);
  }, [itemModal, editItem, inlineEdit, confirm]);
  const closeForm = useCallback(() => setPlanModal(null), []);

  const planCols = [
    {
      key: 'title', header: 'Care plan', sortable: true,
      render: (p) => (
        <span className="min-w-0 block">
          <span className="font-medium text-slate-900">{p.title}</span>
          {p.plan_type === 'reablement' && (
            <span className="block text-[12px] text-teal-800">
              Wellness and reablement{p.reablement_end ? `, to ${fmtDate(p.reablement_end)}` : ''}{p.outcome ? `: ${outcomeLabel(p.outcome)}` : ''}
            </span>
          )}
        </span>
      ),
    },
    { key: 'status', header: 'Status', width: 120, render: (p) => <PlanStatus status={p.status} /> },
    { key: 'item_count', header: 'Planned services', align: 'right', sortable: true, sortValue: (p) => Number(p.item_count) || 0, render: (p) => <span className="tabular-nums">{p.item_count}</span> },
    { key: 'start_date', header: 'Started', sortable: true, render: (p) => fmtDate(p.start_date) },
    { key: 'review_due', header: 'Review due', sortable: true, render: (p) => <Deadline date={p.review_due} done={p.status === 'closed'} /> },
  ];

  const itemCols = [
    {
      key: 'service_name', header: 'Service',
      render: (it) => (
        <div className="min-w-0">
          <div className="font-medium text-slate-800">{it.service_name}</div>
          {it.service_code ? <div className="text-[11px] font-mono text-slate-500">{it.service_code}</div> : null}
          {it.notes ? <div className="text-[12px] text-slate-500 whitespace-pre-wrap">{it.notes}</div> : null}
        </div>
      ),
    },
    { key: 'category', header: 'Category', width: 130, render: (it) => <Category value={it.category} /> },
    { key: 'planned_frequency', header: 'Frequency', render: (it) => it.planned_frequency || '—' },
    { key: 'planned_quantity', header: 'Planned quantity', align: 'right', render: (it) => (it.planned_quantity ? <span className="tabular-nums">{it.planned_quantity} {it.unit || ''}</span> : '—') },
  ];

  const planForm = (
    <div className="space-y-4">
      <Field label="Title" required>{(id) => <input id={id} className={INP} value={pf.title || ''} onChange={(e) => setPf((p) => ({ ...p, title: e.target.value }))} />}</Field>
      <Field label="Kind of plan" hint="A wellness and reablement period is short-term support to regain independence, with an end date and an outcome.">
        {(id) => (
          <select id={id} className={INP} value={pf.plan_type || 'standard'} onChange={(e) => setPf((p) => ({ ...p, plan_type: e.target.value }))}>
            <option value="standard">Ongoing care plan</option>
            <option value="reablement">Wellness and reablement period</option>
          </select>
        )}
      </Field>
      <Field label="Summary" hint="A short, plain description of the plan for the participant.">
        {(id) => <textarea id={id} rows={2} className={INP} value={pf.summary || ''} onChange={(e) => setPf((p) => ({ ...p, summary: e.target.value }))} placeholder="Support to stay safe at home, with help with showering and shopping each week, for example" />}
      </Field>
      <Field label="Goals">{(id) => <textarea id={id} rows={3} className={INP} value={pf.goals || ''} onChange={(e) => setPf((p) => ({ ...p, goals: e.target.value }))} placeholder="The participant's goals from their assessment" />}</Field>
      <Field label="Clinical notes">{(id) => <textarea id={id} rows={2} className={INP} value={pf.clinical_notes || ''} onChange={(e) => setPf((p) => ({ ...p, clinical_notes: e.target.value }))} />}</Field>
      <FormRow cols={3}>
        <Field label="Status">
          {(id) => (
            <select id={id} className={INP} value={pf.status || 'draft'} onChange={(e) => setPf((p) => ({ ...p, status: e.target.value }))}>
              <option value="draft">Draft</option><option value="active">Active</option><option value="under_review">Under review</option><option value="closed">Closed</option>
            </select>
          )}
        </Field>
        <Field label="Start">{(id) => <input id={id} type="date" className={INP} value={pf.start_date || ''} onChange={(e) => setPf((p) => ({ ...p, start_date: e.target.value }))} />}</Field>
        <Field label="Review due">{(id) => <input id={id} type="date" className={INP} value={pf.review_due || ''} onChange={(e) => setPf((p) => ({ ...p, review_due: e.target.value }))} />}</Field>
      </FormRow>
      {pf.plan_type === 'reablement' && (
        <div className="rounded-lg border border-slate-200 p-3 space-y-3">
          <FormRow cols={2}>
            <Field label="Period ends" required hint="Usually up to 12 weeks from the start.">
              {(id) => <input id={id} type="date" className={INP} value={pf.reablement_end || ''} onChange={(e) => setPf((p) => ({ ...p, reablement_end: e.target.value }))} />}
            </Field>
            <Field label="Outcome" hint="Recorded when the period ends.">
              {(id) => (
                <select id={id} className={INP} value={pf.outcome || ''} onChange={(e) => setPf((p) => ({ ...p, outcome: e.target.value }))}>
                  <option value="">Not recorded yet</option>
                  {OUTCOMES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              )}
            </Field>
          </FormRow>
          {pf.outcome && (
            <Field label="What changed">
              {(id) => <textarea id={id} rows={2} className={INP} value={pf.outcome_notes || ''} onChange={(e) => setPf((p) => ({ ...p, outcome_notes: e.target.value }))} placeholder="What the participant can now do, and any support that continues" />}
            </Field>
          )}
        </div>
      )}
    </div>
  );
  const formFooter = (
    <>
      <button type="button" onClick={closeForm} className="btn-secondary text-sm">Cancel</button>
      <button type="button" onClick={savePlan} disabled={saving} className="btn-primary text-sm">
        {saving ? 'Saving' : (planModal && planModal.editing ? 'Save changes' : 'Create care plan')}
      </button>
    </>
  );

  const items = selected ? (selected.items || []) : [];

  return (
    <div>
      {!embedded && <PageHead icon={ClipboardList}
        title="Care plans"
        purpose="Each participant's goals, the services planned to meet them, and when the plan is next due for review."
        actions={<>
          <select value={clientId} onChange={(e) => setClientId(e.target.value)} aria-label="Participant"
            className={clsx(INP, '!w-auto min-w-[240px]')}>
            <option value="">Select a participant</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {clientId && !selected && <button type="button" onClick={newPlan} className="btn-primary text-sm"><Plus size={16} /> New care plan</button>}
        </>}
      />}

      {!embedded && <NeedsReviews />}

      {!clientId ? (
        <Register
          title="Every participant's care plans" count={allPlans ? allPlans.length : undefined} countLabel={allPlans && allPlans.length === 1 ? 'plan' : 'plans'}
          columns={[{ key: 'client_name', header: 'Participant', sortable: true, render: (p) => <span className="font-medium text-slate-900">{p.client_name}</span> }, ...planCols]}
          rows={allPlans || []} loading={loading && !allPlans} error={loadError} onRetry={loadPlans} rowTone={reviewTone}
          onRowClick={(p) => { setClientId(p.client_id); openPlan(p.id); }}
          empty={{ icon: ClipboardList, title: 'No care plans yet', hint: 'Choose a participant above to write their first care plan.' }}
        />
      ) : (
        <Register
          title="Care plans" count={plans.length} countLabel={plans.length === 1 ? 'plan' : 'plans'}
          actions={embedded && !selected ? <button type="button" onClick={newPlan} className="btn-secondary btn-sm"><Plus size={14} /> New care plan</button> : undefined}
          error={loadError} onRetry={loadPlans}
          columns={planCols} rows={plans} loading={loading}
          onRowClick={(p) => openPlan(p.id)} activeRowKey={selected ? selected.id : null} rowTone={reviewTone}
          rowActions={(p) => (
            <>
              <button type="button" onClick={(e) => { e.stopPropagation(); editPlan(p); }} title="Edit" aria-label="Edit care plan" className="btn-ghost btn-sm !px-1.5"><Pencil size={14} /></button>
              <button type="button" onClick={(e) => { e.stopPropagation(); delPlan(p); }} title="Delete" aria-label="Delete care plan" className="btn-ghost btn-sm !px-1.5 hover:!text-red-700"><Trash2 size={14} /></button>
            </>
          )}
          empty={{
            icon: Target,
            title: 'No care plans yet',
            hint: 'Create a care plan to map this participant’s goals to the services that will meet them.',
            action: <button type="button" onClick={newPlan} className="btn-primary btn-sm"><Plus size={14} /> New care plan</button>,
          }}
        />
      )}

      {/* The open plan */}
      <DetailDrawer
        open={!!selected} onClose={closeDrawer} icon={ClipboardList} width="xl"
        title={selected ? selected.title : ''}
        statusNode={selected ? <PlanStatus status={selected.status} /> : null}
        meta={selected ? [
          { label: 'Started', value: fmtDate(selected.start_date) },
          { label: 'Review due', value: <Deadline date={selected.review_due} done={selected.status === 'closed'} /> },
          { label: 'Planned services', value: items.length },
          ...(selected.plan_type === 'reablement' ? [
            { label: 'Reablement ends', value: <Deadline date={selected.reablement_end} done={!!selected.outcome || selected.status === 'closed'} /> },
            { label: 'Outcome', value: outcomeLabel(selected.outcome) || 'Not recorded yet' },
          ] : []),
        ] : undefined}
        footer={selected ? (inlineEdit ? formFooter : (
          <button type="button" onClick={() => editPlan(selected)} className="btn-secondary text-sm"><Pencil size={14} /> Edit plan</button>
        )) : null}
      >
        {selected && (inlineEdit ? planForm : (
          <div className="space-y-4">
            {selected.summary && (
              <Panel title="Summary" bodyClass="px-4 py-3">
                <p className="text-[13px] text-slate-700 whitespace-pre-wrap">{selected.summary}</p>
              </Panel>
            )}
            {selected.goals && (
              <Panel title="Goals" icon={Target} bodyClass="px-4 py-3">
                <p className="text-[13px] text-slate-700 whitespace-pre-wrap">{selected.goals}</p>
              </Panel>
            )}
            {selected.clinical_notes && (
              <Panel title="Clinical notes" bodyClass="px-4 py-3">
                <p className="text-[13px] text-slate-700 whitespace-pre-wrap">{selected.clinical_notes}</p>
              </Panel>
            )}
            {selected.plan_type === 'reablement' && selected.outcome_notes && (
              <Panel title="What changed" bodyClass="px-4 py-3">
                <p className="text-[13px] text-slate-700 whitespace-pre-wrap">{selected.outcome_notes}</p>
              </Panel>
            )}
            {itemModal && (
              <AddItemModal planId={selected.id} onClose={() => setItemModal(false)} onSaved={() => { setItemModal(false); openPlan(selected.id); loadPlans(); }} />
            )}
            {editItem && (
              <EditItemForm key={editItem.id} item={editItem} onClose={() => setEditItem(null)} onSaved={() => { setEditItem(null); openPlan(selected.id); }} />
            )}
            <Register
              title="Planned services" count={items.length} dense
              columns={itemCols} rows={items}
              actions={!itemModal && <button type="button" onClick={startAdd} className="btn-primary btn-sm"><Plus size={14} /> Add service</button>}
              activeRowKey={editItem ? editItem.id : null}
              rowActions={(it) => (
                <>
                  <button type="button" onClick={() => startEdit(it)} title="Edit" aria-label={`Edit ${it.service_name || 'planned service'}`} className="btn-ghost btn-sm !px-1.5"><Pencil size={14} /></button>
                  <button type="button" onClick={() => delItem(it)} title="Remove" aria-label="Remove planned service" className="btn-ghost btn-sm !px-1.5 hover:!text-red-700"><Trash2 size={14} /></button>
                </>
              )}
              empty={{
                icon: Target,
                title: 'No planned services yet',
                hint: 'Add a service from the service list and say how often it should be delivered.',
                action: !itemModal && <button type="button" onClick={startAdd} className="btn-primary btn-sm"><Plus size={14} /> Add service</button>,
              }}
            />
            <Panel title={`Medications${meds.length ? ` (${meds.length})` : ''}`} icon={Pill} bodyClass="px-4 py-3">
              {meds.length === 0 ? (
                <p className="text-[13px] text-slate-500">No current medications on the medication chart.</p>
              ) : (
                <ul className="space-y-1.5 text-[13px]">
                  {meds.map((m) => (
                    <li key={m.id} className="flex flex-wrap justify-between gap-2">
                      <span className="font-medium text-slate-800">{m.medication_name}{m.is_prn ? ' (as needed)' : ''}</span>
                      <span className="text-slate-500">{[m.dosage, m.route, m.schedule || m.frequency].filter(Boolean).join(' · ')}</span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-2 text-[12px] text-slate-500">Workers see this list on each visit in the mobile app. Doses are recorded on the medication chart.</p>
            </Panel>
          </div>
        ))}
      </DetailDrawer>

      {/* A new plan, or an edit started from the list */}
      <DetailDrawer
        open={formDrawer} onClose={closeForm} icon={ClipboardList} width="md"
        title={planModal && planModal.editing ? 'Edit care plan' : 'New care plan'}
        footer={formFooter}
      >
        {formDrawer && planForm}
      </DetailDrawer>
    </div>
  );
}

// Adding a planned service happens inside the plan's drawer, as a panel above the list, so it never
// has to fight the drawer for the top of the stack. The name is kept; the surface changed.
function AddItemModal({ planId, onClose, onSaved }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [freq, setFreq] = useState('');
  const [qty, setQty] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => { api.get('/aged-care/service-items', { params: { search: q.trim() } }).then((r) => setResults(r.data || [])).catch(() => {}); }, 250);
    return () => clearTimeout(t);
  }, [q]);
  const save = async () => {
    if (!picked) return toast.error('Pick a service item');
    setSaving(true);
    try {
      await api.post(`/aged-care/care-plans/${planId}/items`, { service_code: picked.code, service_name: picked.name, category: picked.category, unit: picked.unit, planned_frequency: freq || null, planned_quantity: qty === '' ? null : Number(qty), notes: notes.trim() || null });
      toast.success('Service added');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not add the service'); setSaving(false); }
  };
  return (
    <Panel
      title="Add planned service" icon={Plus} tone="muted" bodyClass="px-4 py-3 space-y-3"
      actions={<button type="button" onClick={onClose} aria-label="Close" className="btn-ghost btn-sm !px-1.5"><X size={14} /></button>}
    >
      {picked ? (
        <div className="flex items-center justify-between rounded-lg border border-primary-200 bg-primary-50 px-3 py-2">
          <span className="text-[13px] font-medium text-primary-700">{picked.name}</span>
          <button type="button" onClick={() => setPicked(null)} aria-label="Choose a different service" className="btn-ghost btn-sm !px-1.5"><X size={14} /></button>
        </div>
      ) : (
        <Field label="Service item" hint={q.trim().length >= 2 && results.length === 0 ? 'No matches. Add items in the service list tab first.' : undefined}>
          {(id) => (
            <>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input id={id} autoFocus className={`${INP} !pl-8`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the service list" />
              </div>
              {results.length > 0 && (
                <div className="mt-2 border border-slate-200 rounded-lg max-h-52 overflow-y-auto divide-y divide-slate-100 bg-white">
                  {results.map((it) => (
                    <button key={it.id} type="button" onClick={() => { setPicked(it); setResults([]); setQ(''); }} className="w-full text-left px-3 py-2 hover:bg-slate-50">
                      <span className="block text-[13px] font-medium text-slate-800">{it.name}</span>
                      <span className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5">
                        <span className="font-mono">{it.code}</span>
                        <Category value={it.category} />
                        <span className="tabular-nums">{money(it.price_cap)} a {it.unit}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </Field>
      )}
      <FormRow cols={2}>
        <Field label="Frequency">{(id) => <input id={id} className={INP} value={freq} onChange={(e) => setFreq(e.target.value)} placeholder="Twice a week, for example" />}</Field>
        <Field label="Planned quantity">{(id) => <input id={id} type="number" step="0.5" className={INP} value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Hours or units" />}</Field>
      </FormRow>
      <Field label="Notes">{(id) => <textarea id={id} rows={2} className={INP} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the worker should know about this service" />}</Field>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="btn-secondary btn-sm">Cancel</button>
        <button type="button" onClick={save} disabled={saving || !picked} className="btn-primary btn-sm"><Plus size={14} /> {saving ? 'Adding' : 'Add service'}</button>
      </div>
    </Panel>
  );
}

// Correcting a planned service in the same place a new one is added. The name, category, how often,
// how much and the notes can change; the service code stays as it was picked from the service list.
function EditItemForm({ item, onClose, onSaved }) {
  const [f, setF] = useState(() => ({
    service_name: item.service_name || '',
    category: normaliseCategory(item.category) || 'independence',
    planned_frequency: item.planned_frequency || '',
    planned_quantity: item.planned_quantity != null ? String(Number(item.planned_quantity)) : '',
    unit: item.unit || 'hour',
    notes: item.notes || '',
  }));
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    if (!f.service_name.trim()) return toast.error('Name the service');
    setSaving(true);
    try {
      await api.put(`/aged-care/care-plan-items/${item.id}`, {
        service_name: f.service_name.trim(),
        category: f.category,
        planned_frequency: f.planned_frequency.trim() || null,
        planned_quantity: f.planned_quantity === '' ? null : Number(f.planned_quantity),
        unit: f.unit.trim() || 'hour',
        notes: f.notes.trim() || null,
      });
      toast.success('Saved');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save the service'); setSaving(false); }
  };
  return (
    <Panel
      title="Edit planned service" icon={Pencil} tone="muted" bodyClass="px-4 py-3 space-y-3"
      actions={<button type="button" onClick={onClose} aria-label="Close" className="btn-ghost btn-sm !px-1.5"><X size={14} /></button>}
    >
      <FormRow cols={2}>
        <Field label="Service" required>{(id) => <input id={id} autoFocus className={INP} value={f.service_name} onChange={set('service_name')} />}</Field>
        <Field label="Category">{(id) => (
          <select id={id} className={INP} value={f.category} onChange={set('category')}>
            {Object.entries(CATEGORY_HUE).map(([k, c]) => <option key={k} value={k}>{c.label}</option>)}
          </select>
        )}</Field>
      </FormRow>
      <FormRow cols={3}>
        <Field label="How often">{(id) => <input id={id} className={INP} value={f.planned_frequency} onChange={set('planned_frequency')} placeholder="Twice a week, for example" />}</Field>
        <Field label="Planned quantity">{(id) => <input id={id} type="number" min="0" step="0.5" className={INP} value={f.planned_quantity} onChange={set('planned_quantity')} placeholder="Hours or units" />}</Field>
        <Field label="Unit">{(id) => <input id={id} className={INP} maxLength={20} value={f.unit} onChange={set('unit')} placeholder="hour" />}</Field>
      </FormRow>
      <Field label="Notes">{(id) => <textarea id={id} rows={2} className={INP} value={f.notes} onChange={set('notes')} placeholder="Anything the worker should know about this service" />}</Field>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="btn-secondary btn-sm">Cancel</button>
        <button type="button" onClick={save} disabled={saving} className="btn-primary btn-sm">{saving ? 'Saving' : 'Save changes'}</button>
      </div>
    </Panel>
  );
}
